import React from 'react';
import { Button, Form, ButtonGroup } from 'react-bootstrap';
import { useTheme } from '../../context/ThemeContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Title, Tooltip, Legend);

// Hex values matching .sensor-card / .digital-card gradients (Chart.js cannot use CSS vars on canvas)
const CARD_CHART_COLORS = {
  sensor: { line: '#9244d2', fill: 'rgba(146, 68, 210, 0.25)' },
  digital: { line: '#44cbd2', fill: 'rgba(68, 203, 210, 0.25)' },
};

const activePillStyle = (lineColor, isActive) =>
  isActive
    ? { backgroundColor: lineColor, borderColor: lineColor, color: 'white' }
    : { borderColor: lineColor, color: lineColor, backgroundColor: 'transparent' };

const DeviceHistoryChart = ({
  expandedSensor,
  cardVariant = 'sensor',
  history,
  selectedPeriod,
  setSelectedPeriod,
  customStartDate,
  customEndDate,
  setCustomStartDate,
  setCustomEndDate,
  device,
  getFieldName,
  getDefaultCustomDates,
}) => {
  const { isDark } = useTheme();

  if (!expandedSensor) return null;

  const now = new Date();
  let timeFilter = () => true;
  switch (selectedPeriod) {
    case 'live':
      timeFilter = (d) => now - new Date(d.timestamp) < 1 * 60 * 1000;
      break;
    case '1hour':
      timeFilter = (d) => now - new Date(d.timestamp) < 1 * 60 * 60 * 1000;
      break;
    case '1day':
      timeFilter = (d) => now - new Date(d.timestamp) < 24 * 60 * 60 * 1000;
      break;
    default:
      break;
  }
  const filteredHistory = history.filter(timeFilter);

  const sortedHistory = [...filteredHistory].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const dataKey = getFieldName(expandedSensor);
  const filteredPoints = sortedHistory.filter((d) => d.data[dataKey] !== undefined);

  const variant = cardVariant === 'digital' ? 'digital' : 'sensor';
  const { line: lineColor, fill: fillColor } = CARD_CHART_COLORS[variant];
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';
  const tickColor = isDark ? '#9a9a9a' : '#6a6a6a';

  const chartData = {
    labels: filteredPoints.map((d) => new Date(d.timestamp)),
    datasets: [{
      label: expandedSensor.name || expandedSensor.sensorType,
      data: filteredPoints.map((d) => (typeof d.data[dataKey] === 'object' ? null : d.data[dataKey])),
      borderColor: lineColor,
      backgroundColor: fillColor,
      tension: 0.3,
      pointRadius: 2,
      spanGaps: true,
    }],
  };

  let unit;
  let stepSize;
  let displayFormat;
  switch (selectedPeriod) {
    case 'live':
      unit = 'second';
      stepSize = 6;
      displayFormat = { second: 'mm:ss' };
      break;
    case '1hour':
      unit = 'minute';
      stepSize = 5;
      displayFormat = { minute: 'HH:mm' };
      break;
    case '1day':
      unit = 'minute';
      stepSize = 12;
      displayFormat = { minute: 'HH:mm' };
      break;
    case 'custom':
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        const diffDays = (end - start) / (1000 * 60 * 60 * 24);

        if (diffDays <= 1) {
          unit = 'hour';
          stepSize = 1;
          displayFormat = { hour: 'HH:mm' };
        } else if (diffDays <= 7) {
          unit = 'day';
          stepSize = 1;
          displayFormat = { day: 'MM/dd' };
        } else if (diffDays <= 31) {
          unit = 'day';
          stepSize = 2;
          displayFormat = { day: 'MM/dd' };
        } else if (diffDays <= 183) {
          unit = 'month';
          stepSize = 1;
          displayFormat = { month: 'MMM yyyy' };
        } else {
          unit = 'month';
          stepSize = 2;
          displayFormat = { month: 'MMM yyyy' };
        }
      } else {
        unit = 'day';
        stepSize = 1;
        displayFormat = { day: 'MM/dd' };
      }
      break;
    default:
      unit = 'minute';
      stepSize = 1;
      displayFormat = { minute: 'HH:mm' };
  }

  let min;
  let max;
  let dynamicTickCallback = null;

  switch (selectedPeriod) {
    case 'live':
      min = now.getTime() - 1 * 60 * 1000;
      max = now.getTime();
      break;
    case '1hour':
      min = now.getTime() - 1 * 60 * 60 * 1000;
      max = now.getTime();
      break;
    case '1day':
      min = now.getTime() - 24 * 60 * 60 * 1000;
      max = now.getTime();
      break;
    case 'custom':
      if (customStartDate && customEndDate) {
        min = new Date(filteredHistory[filteredHistory.length - 1].timestamp).getTime() - 60 * 60 * 1000;
        max = new Date(filteredHistory[0].timestamp).getTime() + 60 * 60 * 1000;

        const startDate = new Date(customStartDate);
        const endDate = new Date(customEndDate);
        const dateRangeMs = endDate.getTime() - startDate.getTime();
        const dateRangeDays = dateRangeMs / (1000 * 60 * 60 * 24);

        dynamicTickCallback = function tick(value) {
          const date = new Date(value);

          if (dateRangeDays <= 1) {
            return date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            });
          }
          if (dateRangeDays <= 7) {
            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              hour12: true,
            });
          }
          if (dateRangeDays <= 31) {
            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            });
          }
          return date.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
          });
        };
      } else {
        min = now.getTime() - 7 * 24 * 60 * 60 * 1000;
        max = now.getTime();
      }
      break;
    default:
      min = now.getTime() - 1 * 60 * 60 * 1000;
      max = now.getTime();
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        min,
        max,
        time: { unit, stepSize, displayFormats: displayFormat },
        title: { display: true, text: 'Time', color: tickColor },
        grid: { color: gridColor },
        ticks: {
          color: tickColor,
          callback: dynamicTickCallback || function tick(value) {
            const diff = now - value;
            switch (selectedPeriod) {
              case 'live': {
                const seconds = Math.floor(diff / 1000);
                return `${seconds}s ago`;
              }
              case '1hour': {
                const minutes = Math.floor(diff / 60000);
                return `${minutes} min ago`;
              }
              case '1day': {
                const hours = Math.floor(diff / 3600000);
                return `${hours} hr ago`;
              }
              default:
                return '';
            }
          },
        },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Value', color: tickColor },
        grid: { color: gridColor },
        ticks: { color: tickColor },
      },
    },
    plugins: {
      legend: { display: false },
      decimation: {
        enabled: true,
        algorithm: 'lttb',
        samples: 60,
      },
    },
  };

  const periodLabel = selectedPeriod === 'live'
    ? 'Live'
    : selectedPeriod === '1hour'
      ? 'Last Hour'
      : selectedPeriod === '1day'
        ? 'Last Day'
        : selectedPeriod === 'custom'
          ? `Custom Range (${customStartDate} to ${customEndDate})`
          : 'Custom Range';

  return (
    <div className="app-chart-panel mt-4 p-4">
      <h3 className="mb-3" style={{ fontFamily: 'var(--font-display)', color: lineColor }}>
        {expandedSensor.name || expandedSensor.sensorType} — {periodLabel}
      </h3>
      <div className="d-flex justify-content-center mb-3 flex-wrap align-items-center gap-2 period-pills">
        <ButtonGroup size="sm">
          {[
            { key: 'live', label: 'Live' },
            { key: '1hour', label: '1 Hour' },
            { key: '1day', label: '1 Day' },
          ].map(({ key, label }) => (
            <Button
              key={key}
              size="sm"
              className={selectedPeriod === key ? 'active' : ''}
              style={activePillStyle(lineColor, selectedPeriod === key)}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPeriod(key);
                setCustomStartDate('');
                setCustomEndDate('');
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <Button
            size="sm"
            className={selectedPeriod === 'custom' ? 'active' : ''}
            style={activePillStyle(lineColor, selectedPeriod === 'custom')}
            onClick={(e) => {
              e.stopPropagation();
              const defaultDates = getDefaultCustomDates();
              setCustomStartDate(defaultDates.start);
              setCustomEndDate(defaultDates.end);
              setSelectedPeriod('custom');
            }}
          >
            Custom Range
          </Button>
          {selectedPeriod === 'custom' && (
            <>
              <Form.Control
                type="date"
                size="sm"
                style={{ width: '140px' }}
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
              <span className="text-muted">to</span>
              <Form.Control
                type="date"
                size="sm"
                style={{ width: '140px' }}
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </>
          )}
        </div>
      </div>
      <div style={{ height: '400px' }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default DeviceHistoryChart;